import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { features, whyItems, supplyItems, partners, galleryImages, heroImage, secondaryImage } from "../mock";
import { homeDetailPhotos } from "../data/uploadedPhotos";
import { useTranslation } from "react-i18next";

const Home = () => {
  const { t } = useTranslation();
  return (
    <main className="pt-[72px]">
      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pt-8 md:pt-12">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
          <div className="max-w-[280px] reveal">
            <span data-testid="home-intro-badge" className="inline-block px-4 py-1.5 rounded-full border border-black/25 text-[10px] font-medium uppercase tracking-[0.16em] mb-6">
              {t("home.badge")}
            </span>
            <p data-testid="home-intro-text" className="text-[13px] leading-relaxed text-[#0e0e10]/60">
              {t("home.intro")}
            </p>
          </div>
        </div>

        <h1 data-testid="home-heading" className="font-display font-bold tracking-tight leading-[0.86] mt-2 md:-mt-6 text-[clamp(4rem,18vw,17rem)] reveal" style={{ animationDelay: "0.15s" }}>
          ORICO
        </h1>
      </section>

      {/* HERO IMAGE */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-4">
        <div data-testid="home-hero-media" className="reveal-media relative overflow-hidden rounded-sm aspect-[4/5] sm:aspect-[4/3]">
          <img src={heroImage} data-testid="home-hero-image" alt={t("photos.gray-living-room")} width={564} height={805} fetchPriority="high" className="w-full h-full absolute inset-0 object-cover object-[center_65%]" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <div className="absolute bottom-6 left-6 md:bottom-8 md:left-8">
            <p data-testid="home-hero-caption" className="text-white font-display text-lg md:text-2xl uppercase tracking-wide leading-tight whitespace-pre-line">
              {t("home.heroCaption")}
            </p>
          </div>
          <Link to="/projekti" data-testid="home-hero-projects-link" aria-label={t("common.viewProjects")} className="group absolute top-6 right-6 md:top-8 md:right-8 w-14 h-14 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors">
            <ArrowUpRight size={22} className="hover-arrow text-[#0e0e10]" />
          </Link>
        </div>
      </section>

      {/* STATEMENT */}
      <section data-testid="home-studio-statement" className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <p className="reveal-on-scroll font-display font-medium text-[clamp(1.6rem,4.4vw,3.6rem)] leading-[1.12] tracking-tight text-balance">
          <span data-testid="home-statement-lead" className="text-[#0e0e10]">{t("home.statementLead")} </span>
          <span data-testid="home-statement-detail" className="text-[#0e0e10]/35">{t("home.statementMuted")}</span>
        </p>
      </section>

      {/* FULL-WIDTH PARALLAX BAND 1 */}
      <section data-testid="home-materials-band" className="reveal-media parallax-wrap relative overflow-hidden mb-20 md:mb-28 aspect-[4/3] sm:aspect-[21/9]">
        <img src={galleryImages[1]} data-testid="home-materials-image" data-parallax="0.18" alt={t("common.interiorDetail")} className="parallax-img absolute inset-x-0 -top-[15%] w-full h-[130%] object-cover" />
        <div className="absolute inset-0 bg-black/25" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1400px] w-full mx-auto px-5 md:px-10 pb-8 md:pb-12">
            <p data-testid="home-materials-label" className="text-white/70 text-[11px] uppercase tracking-[0.18em] mb-2">{t("home.materialsLabel")}</p>
            <p data-testid="home-materials-title" className="text-white font-display text-2xl md:text-4xl tracking-tight leading-tight max-w-2xl">
              {t("home.materialsTitle")}
            </p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-32">
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="flex flex-col gap-4">
            {features.map((f, i) => (
              <div
                key={f.id}
                style={{ "--reveal-delay": `${i * 110}ms` }}
                className={`reveal-on-scroll group rounded-sm p-7 md:p-8 flex items-start justify-between transition-all duration-300 border ${
                  f.dark
                    ? "bg-[#0e0e10] text-white border-[#0e0e10]"
                    : "bg-white/60 border-black/10 hover:border-black/30"
                }`}
              >
                <div className="min-w-0">
                  <h3 data-testid={`home-feature-title-${f.id}`} className="font-display text-xl md:text-2xl leading-tight tracking-tight">
                    {t(`features.${i}.title`)}<br />{t(`features.${i}.subtitle`)}
                  </h3>
                  <p data-testid={`home-feature-description-${f.id}`} className={`text-[12px] mt-4 max-w-[300px] uppercase tracking-wide ${f.dark ? "text-white/55" : "text-[#0e0e10]/50"}`}>
                    {t(`features.${i}.desc`)}
                  </p>
                </div>
                <ArrowUpRight data-testid={`home-feature-arrow-${f.id}`} size={22} className="hover-arrow text-[#0057FF]" />
              </div>
            ))}
          </div>
          <div className="reveal-media parallax-wrap img-zoom-wrap relative overflow-hidden rounded-sm min-h-[400px] grayscale">
            <img src={secondaryImage} data-testid="home-features-image" data-parallax="0.14" alt={t("common.interior")} className="parallax-img w-full h-[124%] -top-[12%] object-cover absolute inset-x-0" />
          </div>
        </div>
      </section>

      {/* SUPPLY */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-24">
        <div className="reveal-on-scroll flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <h2 data-testid="home-supply-heading" className="font-display font-bold text-[clamp(2rem,6vw,4.5rem)] leading-[0.92] tracking-tight whitespace-pre-line">
            {t("home.supplyTitle")}
          </h2>
          <p data-testid="home-supply-intro" className="text-[13px] text-[#0e0e10]/55 max-w-sm leading-relaxed mb-2">
            {t("home.supplyIntro")}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1">
          {supplyItems.map((s, i) => (
            <div key={s.title} style={{ "--reveal-delay": `${(i % 3) * 90}ms` }} className="reveal-on-scroll group border-t border-black/12 py-5 hover:border-black/40 transition-colors flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 data-testid={`home-supply-title-${i}`} className="font-display text-lg tracking-tight">{t(`supply.${i}.title`)}</h3>
                <p data-testid={`home-supply-description-${i}`} className="text-[12px] text-[#0e0e10]/50 mt-1">{t(`supply.${i}.desc`)}</p>
              </div>
              <ArrowUpRight data-testid={`home-supply-arrow-${i}`} size={17} className="hover-arrow text-[#0e0e10]/30 group-hover:text-[#0057FF] shrink-0 mt-1" />
            </div>
          ))}
        </div>
      </section>

      {/* INTERIOR IMAGE */}
      <section data-testid="home-interior-section" className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-28">
        <div data-testid="home-interior-media" className="reveal-media parallax-wrap img-zoom-wrap relative overflow-hidden rounded-sm aspect-[4/5] md:aspect-[16/9]">
          <img src={galleryImages[2]} data-testid="home-interior-image" data-parallax="0.16" alt={t("common.interior")} className="parallax-img w-full h-[126%] -top-[13%] object-cover absolute inset-x-0" />
        </div>
      </section>

      {/* PARTNERS MARQUEE */}
      <section data-testid="home-partners-section" className="border-y border-black/10 py-8 overflow-hidden">
        <p data-testid="home-partners-heading" className="max-w-[1400px] mx-auto px-5 md:px-10 text-[11px] uppercase tracking-[0.18em] text-[#0e0e10]/45 mb-6">
          {t("home.partnersIntro")}
        </p>
        <div data-testid="home-partners-track" className="flex w-max whitespace-nowrap animate-marquee">
          {[0, 1].map((k) => (
            <div key={k} data-testid={`home-partners-group-${k}`} aria-hidden={k === 1} className="flex shrink-0 items-center">
              {partners.map((p) => (
                <span key={p + k} data-testid={`partner-name-${k}-${p.toLowerCase().replace(/\s+/g, "-")}`} className="font-display text-2xl md:text-3xl font-semibold tracking-tight mx-6 text-[#1f3b66]">
                  {p}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* WHY ORICO */}
      <section className="bg-[#0e0e10] text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <div className="reveal-on-scroll flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <h2 data-testid="home-why-heading" className="font-display font-bold text-[clamp(2.5rem,8vw,6rem)] leading-[0.9] tracking-tight whitespace-pre-line">
              {t("home.whyTitle")}
            </h2>
            <span data-testid="home-why-badge" className="inline-block px-4 py-1.5 rounded-full border border-white/25 text-[10px] font-medium uppercase tracking-[0.16em] mb-3">
              {t("home.whyBadge")}
            </span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {whyItems.map((w, i) => (
              <div key={w.no} style={{ "--reveal-delay": `${i * 120}ms` }} className="reveal-on-scroll border-t border-white/15 pt-6">
                <div data-testid={`home-value-number-${w.no}`} className="text-[#0057FF] font-display text-sm mb-8">{w.no}</div>
                <h3 data-testid={`home-value-title-${w.no}`} className="font-display text-xl mb-3 tracking-tight">{t(`why.${i}.title`)}</h3>
                <p data-testid={`home-value-description-${w.no}`} className="text-[13px] text-white/55 leading-relaxed">{t(`why.${i}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTERIOR GALLERY */}
      <section data-testid="home-details-gallery" className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="reveal-on-scroll flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <h2 data-testid="home-details-heading" className="font-display font-bold text-[clamp(2rem,6vw,4.5rem)] leading-[0.92] tracking-tight whitespace-pre-line">
            {t("home.detailsTitle")}
          </h2>
          <p data-testid="home-details-intro" className="text-[13px] text-[#0e0e10]/55 max-w-sm leading-relaxed mb-2">
            {t("home.detailsIntro")}
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 items-start">
          {homeDetailPhotos.map((photo, idx) => (
            <div
              key={photo.id}
              style={{ "--reveal-delay": `${idx * 130}ms`, aspectRatio: `${photo.width} / ${photo.height}` }}
              className="reveal-media relative overflow-hidden rounded-sm"
            >
              <img src={photo.src} data-testid={`home-detail-${photo.id}`} alt={t(`photos.${photo.id}`)} width={photo.width} height={photo.height} className="w-full h-full object-contain" loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="reveal-on-scroll max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28 text-center">
        <p data-testid="home-contact-eyebrow" className="text-[11px] uppercase tracking-[0.18em] text-[#0e0e10]/50 mb-6">{t("home.ctaLabel")}</p>
        <h2 data-testid="home-contact-heading" className="font-display font-bold text-[clamp(2rem,7vw,5rem)] leading-[0.95] tracking-tight mb-10 whitespace-pre-line">
          {t("home.ctaTitle")}
        </h2>
        <Link to="/kontakt" data-testid="home-contact-link" className="group inline-flex items-center gap-3 h-14 px-8 rounded-full bg-[#0e0e10] text-white text-sm font-medium uppercase tracking-[0.12em] hover:bg-[#0057FF] transition-colors duration-300">
          {t("common.contactUs")} <ArrowUpRight size={18} className="hover-arrow shrink-0" />
        </Link>
      </section>
    </main>
  );
};

export default Home;
